<!-- src/components/CalendarWidget.vue -->
<template>
  <div class="calendar-widget">
    <header class="widget-header">
  <div class="title-group">
    <h3>My Calendar</h3>
    <span v-if="upcomingEvents.length" class="event-count-badge">
      {{ upcomingEvents.length }} upcoming
    </span>
    <!-- Offline Indicator -->
    <span v-if="isOffline" class="offline-badge" title="Showing cached events from local storage">
      ⚡ Offline Mode
    </span>
  </div>

  <!-- Hide "+ New Event" button if there is an error or we are offline -->
  <button 
    v-if="!error && !isOffline" 
    @click="toggleForm" 
    class="btn-toggle" 
    :disabled="loading"
  >
    {{ showForm ? 'Cancel' : '+ New Event' }}
  </button>
</header>

    <!-- Create / Edit Event Form -->
    <form v-if="showForm" @submit.prevent="handleSubmit" class="event-form">
      <h4>{{ editingId ? 'Edit Event' : 'Create New Event' }}</h4>

      <div class="form-grid">
        <div class="form-group full-width">
          <label for="title">Title *</label>
          <input 
            id="title" 
            v-model="form.title" 
            type="text" 
            required 
            placeholder="e.g. Home Inspection" 
          />
        </div>

        <div class="form-group checkbox-group full-width">
          <label for="isAllDay">
            <input 
              id="isAllDay" 
              v-model="form.isAllDay" 
              type="checkbox" 
            />
            All-day event
          </label>
        </div>

        <div class="form-group">
          <label for="startTime">{{ form.isAllDay ? 'Date *' : 'Start Date & Time *' }}</label>
          <input 
            id="startTime" 
            v-model="form.startTime" 
            :type="form.isAllDay ? 'date' : 'datetime-local'" 
            required 
          />
        </div>

        <div class="form-group full-width">
          <label for="description">Description</label>
          <textarea 
            id="description" 
            v-model="form.description" 
            rows="2" 
            placeholder="Add notes, location, or details..."
          ></textarea>
        </div>
      </div>

      <div class="form-actions">
        <button type="submit" class="btn-submit" :disabled="loading">
          {{ loading ? 'Saving...' : (editingId ? 'Update Event' : 'Save Event') }}
        </button>
        <button v-if="editingId" type="button" @click="resetForm" class="btn-cancel">
          Cancel Edit
        </button>
      </div>
    </form>

    <!-- API Error Banner (When Cache is Available) -->
    <div v-if="error && displayedEvents.length" class="status-banner error-banner">
      <div class="banner-content">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <span>Unable to sync with calendar server. Showing cached events.</span>
      </div>
      <button @click="handleRetry" class="btn-retry-inline" :disabled="loading">
        {{ loading ? 'Retrying...' : 'Retry Sync' }}
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading && !events.length" class="status-message loading-state">
      <div class="spinner"></div>
      <span>Connecting to calendar service...</span>
    </div>

    <!-- Full Error Hero State (When API is Down and No Events Exist) -->
    <div v-else-if="error && !displayedEvents.length" class="error-hero-card">
      <div class="error-icon-wrapper">
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 1l22 22"/><path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/><path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/><path d="M10.71 5.05A16 16 0 0 1 22.58 9"/><path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>
      </div>
      <h4>Calendar Service Unavailable</h4>
      <p>We couldn't connect to your calendar server. Please check your network or try reconnecting.</p>
      <button @click="handleRetry" class="btn-retry-hero" :disabled="loading">
        {{ loading ? 'Attempting Connection...' : 'Try Reconnecting' }}
      </button>
    </div>

    <!-- Events Feed -->
    <ul v-else-if="displayedEvents.length" class="events-list">
      <li 
        v-for="event in displayedEvents" 
        :key="event.id" 
        class="event-card-wrapper"
        :class="{ 'is-expanded': expandedId === event.id }"
      >
        <!-- Main Event Card Header -->
        <div class="event-card" @click="toggleExpand(event.id)">
          
          <div class="date-badge" :class="getDateTagClass(event.startTime)">
            <span class="day">{{ getDayNumber(event.startTime) }}</span>
            <span class="month">{{ getMonthName(event.startTime) }}</span>
            <span v-if="event.isAllDay" class="badge-all-day">All Day</span>
            <span v-else class="time">{{ getTimeString(event.startTime) }}</span>
          </div>

          <div class="event-info">
            <div class="title-row">
              <h4>{{ event.title }}</h4>
              <span v-if="getRelativeDateLabel(event.startTime)" class="relative-tag">
                {{ getRelativeDateLabel(event.startTime) }}
              </span>
            </div>
            <p v-if="event.description" class="description-preview">
              {{ event.description }}
            </p>
          </div>

          <div class="event-actions">
            <button 
              @click.stop="startEdit(event)" 
              class="btn-action edit" 
              title="Edit Event"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
            </button>
            <button 
              @click.stop="handleDelete(event.id)" 
              class="btn-action delete" 
              title="Delete Event"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
            <span class="chevron-icon" title="Toggle Details">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </span>
          </div>
        </div>

        <!-- Expanded Drawer -->
        <Transition name="expand">
          <div v-if="expandedId === event.id" class="event-details-drawer">
            <div class="drawer-content">
              <div class="detail-row highlight-row">
                <span class="detail-label">Starts In:</span>
                <span class="detail-value countdown-pill">
                  ⏱️ {{ getTimeRemaining(event.startTime, event.isAllDay) }}
                </span>
              </div>

              <div class="detail-row">
                <span class="detail-label">Schedule:</span>
                <span class="detail-value">
                  {{ formatFullDate(event.startTime) }} 
                  <template v-if="!event.isAllDay">at {{ getTimeString(event.startTime) }}</template>
                </span>
              </div>

              <div class="detail-row" v-if="event.description">
                <span class="detail-label">Notes:</span>
                <p class="detail-description">{{ event.description }}</p>
              </div>

              <div class="drawer-actions">
                <button @click="startEdit(event)" class="btn-drawer-edit">
                  Edit Event
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </li>
    </ul>

    <!-- Empty State -->
    <div v-else-if="!loading" class="empty-state">
      <p>No upcoming events found.</p>
    </div>

    <!-- Footer Controls -->
    <div v-if="upcomingEvents.length > EVENT_LIMIT" class="list-footer">
      <button @click="showAll = !showAll" class="btn-show-more">
        {{ showAll ? 'Show less' : `Show all (${upcomingEvents.length})` }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue';
import { useCalendarEvents } from '@/composables/useCalendarEvents';

const { events, loading, error, fetchEvents, addEvent, updateEvent, deleteEvent } = useCalendarEvents();

const showForm = ref(false);
const editingId = ref(null);
const expandedId = ref(null);
const showAll = ref(false);
const currentTime = ref(new Date());
const isOffline = ref(false);

const LOCAL_CACHE_KEY = 'calendar_events_backup';
let tickerTimer = null;
const EVENT_LIMIT = 5;

const form = reactive({
  title: '',
  description: '',
  startTime: '',
  isAllDay: false,
});

// Cache successful fetches locally
watch(events, (newEvents) => {
  if (newEvents && newEvents.length > 0) {
    localStorage.setItem(LOCAL_CACHE_KEY, JSON.stringify(newEvents));
    isOffline.value = false;
  }
}, { deep: true });

// Handle fetch with local storage fallback
const loadCalendarData = async () => {
  try {
    await fetchEvents();
    isOffline.value = false;
  } catch (err) {
    // If API fails, attempt loading cached data
    const cached = localStorage.getItem(LOCAL_CACHE_KEY);
    if (cached) {
      try {
        events.value = JSON.parse(cached);
        isOffline.value = true;
      } catch (e) {
        console.error('Failed to parse cached calendar events', e);
      }
    }
  }
};

const handleRetry = () => {
  loadCalendarData();
};

onMounted(() => {
  loadCalendarData();
  tickerTimer = setInterval(() => {
    currentTime.value = new Date();
  }, 30000);
});

onUnmounted(() => {
  if (tickerTimer) clearInterval(tickerTimer);
});

const upcomingEvents = computed(() => {
  if (!events.value || !events.value.length) return [];

  const now = currentTime.value;

  return [...events.value]
    .filter(event => {
      if (!event.startTime) return false;

      if (event.isAllDay) {
        const [datePart] = event.startTime.split('T');
        const [year, month, day] = datePart.split('-');
        const endOfAllDay = new Date(year, month - 1, day, 23, 59, 59, 999);
        return endOfAllDay >= now;
      } else {
        const eventTime = new Date(event.startTime);
        return eventTime >= now;
      }
    })
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
});

const displayedEvents = computed(() => {
  if (showAll.value) return upcomingEvents.value;
  return upcomingEvents.value.slice(0, EVENT_LIMIT);
});

const toggleExpand = (id) => {
  expandedId.value = expandedId.value === id ? null : id;
};

const toggleForm = () => {
  if (showForm.value) {
    resetForm();
  } else {
    showForm.value = true;
  }
};

const startEdit = (event) => {
  editingId.value = event.id;
  form.title = event.title;
  form.description = event.description || '';
  form.isAllDay = Boolean(event.isAllDay);
  
  if (event.startTime) {
    if (event.isAllDay) {
      form.startTime = event.startTime.split('T')[0];
    } else {
      const d = new Date(event.startTime);
      const tzOffset = d.getTimezoneOffset() * 60000;
      form.startTime = new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
    }
  } else {
    form.startTime = '';
  }

  showForm.value = true;
};

const resetForm = () => {
  editingId.value = null;
  form.title = '';
  form.description = '';
  form.startTime = '';
  form.isAllDay = false;
  showForm.value = false;
};

const handleSubmit = async () => {
  if (!form.title || !form.startTime) return;

  const payload = {
    title: form.title,
    description: form.description,
    startTime: form.startTime,
    isAllDay: form.isAllDay,
  };

  try {
    if (editingId.value) {
      await updateEvent(editingId.value, payload);
    } else {
      await addEvent(payload);
    }
    resetForm();
  } catch (err) {
    // Error handled via composable state
  }
};

const handleDelete = async (id) => {
  if (confirm('Are you sure you want to delete this event?')) {
    try {
      await deleteEvent(id);
      if (editingId.value === id) resetForm();
      if (expandedId.value === id) expandedId.value = null;
    } catch (err) {
      // Error handled via composable state
    }
  }
};

// Date helpers
const parseDate = (isoString) => {
  if (!isoString) return new Date();
  const [datePart] = isoString.split('T');
  const [year, month, day] = datePart.split('-');
  return new Date(year, month - 1, day);
};

const getDayNumber = (isoString) => isoString ? parseDate(isoString).getDate() : '';
const getMonthName = (isoString) => isoString ? parseDate(isoString).toLocaleDateString([], { month: 'short' }) : '';
const getTimeString = (isoString) => {
  if (!isoString) return '';
  return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

const formatFullDate = (isoString) => {
  if (!isoString) return '';
  return parseDate(isoString).toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getTimeRemaining = (isoString, isAllDay) => {
  if (!isoString) return '';
  const now = currentTime.value;
  const target = new Date(isoString);

  if (isAllDay) {
    const [datePart] = isoString.split('T');
    const [year, month, day] = datePart.split('-');
    target.setFullYear(year, month - 1, day);
    target.setHours(0, 0, 0, 0);
  }

  const diffMs = target - now;
  if (diffMs <= 0) return 'Today / In progress';

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    const remainingHours = diffHours % 24;
    return `${diffDays} day${diffDays > 1 ? 's' : ''}${remainingHours > 0 ? `, ${remainingHours} hr${remainingHours > 1 ? 's' : ''}` : ''}`;
  }

  if (diffHours > 0) {
    const remainingMins = diffMinutes % 60;
    return `${diffHours} hr${diffHours > 1 ? 's' : ''}${remainingMins > 0 ? `, ${remainingMins} min` : ''}`;
  }

  return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
};

const getRelativeDateLabel = (isoString) => {
  if (!isoString) return null;
  const eventDate = parseDate(isoString);
  const today = new Date(currentTime.value);
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (eventDate.getTime() === today.getTime()) return 'TODAY';
  if (eventDate.getTime() === tomorrow.getTime()) return 'TOMORROW';
  return null;
};

const getDateTagClass = (isoString) => {
  const label = getRelativeDateLabel(isoString);
  if (label === 'TODAY') return 'is-today';
  if (label === 'TOMORROW') return 'is-tomorrow';
  return '';
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/abstract/_variables.scss"; 

.calendar-widget {
  background: #ffffff;
  border: 1px solid mix($light-brown, #ffffff, 40%);
  border-radius: 16px;
  padding: 1.75rem;
  width: 100%;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01);

  .widget-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;

    .title-group {
      display: flex;
      align-items: center;
      gap: 0.75rem;

      h3 {
        margin: 0;
        font-size: 1.35rem;
        font-weight: 700;
        color: $orange;
        letter-spacing: -0.01em;
      }

      .event-count-badge {
        font-size: 0.75rem;
        font-weight: 700;
        background: mix($orange, #ffffff, 15%);
        color: $orange;
        padding: 0.15rem 0.5rem;
        border-radius: 12px;
      }

      .offline-badge {
        font-size: 0.72rem;
        font-weight: 700;
        background: #fef3c7;
        color: #d97706;
        border: 1px solid #fde68a;
        padding: 0.15rem 0.5rem;
        border-radius: 12px;
      }
    }

    .btn-toggle {
      background: #f8fafc;
      border: 1px solid mix($light-brown, $secondary-color, 60%);
      color: $dark-blue;
      border-radius: 8px;
      padding: 0.45rem 1rem;
      font-size: 0.9rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover:not(:disabled) {
        background: $dark-blue;
        color: $white;
        border-color: $dark-blue;
        box-shadow: 0 4px 12px rgba(21, 76, 140, 0.25);
      }

      &:disabled { opacity: 0.5; cursor: not-allowed; }
    }
  }

  /* Status Banner for Partial Errors */
  .status-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.65rem 1rem;
    border-radius: 10px;
    font-size: 0.85rem;
    margin-bottom: 1.25rem;

    &.error-banner {
      background: #fef2f2;
      border: 1px solid #fecaca;
      color: #991b1b;
    }

    .banner-content {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .btn-retry-inline {
      background: #ffffff;
      border: 1px solid #fca5a5;
      color: #991b1b;
      font-size: 0.75rem;
      font-weight: 700;
      padding: 0.25rem 0.6rem;
      border-radius: 6px;
      cursor: pointer;

      &:hover:not(:disabled) { background: #fee2e2; }
      &:disabled { opacity: 0.5; }
    }
  }

  /* Full Hero Error Display */
  .error-hero-card {
    background: #fef2f2;
    border: 1px dashed #fca5a5;
    border-radius: 14px;
    padding: 2.5rem 1.5rem;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    margin: 1rem 0;

    .error-icon-wrapper {
      background: #fee2e2;
      color: #dc2626;
      width: 56px;
      height: 56px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    h4 {
      margin: 0;
      font-size: 1.1rem;
      color: #991b1b;
      font-weight: 700;
    }

    p {
      margin: 0;
      font-size: 0.88rem;
      color: #7f1d1d;
      max-width: 380px;
      line-height: 1.4;
    }

    .btn-retry-hero {
      margin-top: 0.5rem;
      background: #dc2626;
      color: #ffffff;
      border: none;
      border-radius: 8px;
      padding: 0.6rem 1.2rem;
      font-size: 0.85rem;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;

      &:hover:not(:disabled) { background: #b91c1c; }
      &:disabled { opacity: 0.6; cursor: not-allowed; }
    }
  }

  /* Loading State */
  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 2.5rem 0;
    color: $grey-back;
    font-size: 0.9rem;

    .spinner {
      width: 18px;
      height: 18px;
      border: 2px solid mix($light-brown, #ffffff, 60%);
      border-top-color: $dark-blue;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
  }

  .event-form {
    background: mix($white, #ffffff, 40%);
    border: 1px solid mix($light-brown, #ffffff, 50%);
    border-radius: 12px;
    padding: 1.25rem;
    margin-bottom: 1.5rem;

    h4 {
      margin: 0 0 1rem 0;
      font-size: 1rem;
      color: $secondary-color;
    }

    .form-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;

      @media (max-width: 640px) { grid-template-columns: 1fr; }
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 0.35rem;

      &.full-width { grid-column: 1 / -1; }

      &.checkbox-group {
        flex-direction: row;
        align-items: center;

        label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          font-weight: 500;
          color: $secondary-color;
          cursor: pointer;

          input[type="checkbox"] {
            cursor: pointer;
            width: 16px;
            height: 16px;
            accent-color: $dark-blue;
          }
        }
      }

      label {
        font-size: 0.8rem;
        font-weight: 600;
        color: $dark-pink;
        text-transform: uppercase;
        letter-spacing: 0.03em;
      }

      input, textarea {
        padding: 0.6rem 0.75rem;
        border: 1px solid mix($light-brown, $secondary-color, 70%);
        border-radius: 8px;
        font-size: 0.9rem;
        outline: none;
        background: #ffffff;
        color: $secondary-color;
        transition: border-color 0.2s, box-shadow 0.2s;

        &:focus {
          border-color: $mid-blue;
          box-shadow: 0 0 0 3px rgba(29, 78, 216, 0.15);
        }
      }
    }

    .form-actions {
      display: flex;
      gap: 0.75rem;
      margin-top: 1.25rem;

      .btn-submit {
        flex: 1;
        background: $dark-blue;
        color: $white;
        border: none;
        border-radius: 8px;
        padding: 0.65rem;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.2s ease;

        &:hover { background: $mid-blue; }
        &:disabled { opacity: 0.6; cursor: not-allowed; }
      }

      .btn-cancel {
        background: transparent;
        border: 1px solid mix($light-brown, $secondary-color, 70%);
        color: $grey-back;
        border-radius: 8px;
        padding: 0.65rem 1rem;
        font-weight: 500;
        cursor: pointer;

        &:hover { background: $white; color: $secondary-color; }
      }
    }
  }

  .events-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;

    .event-card-wrapper {
      border: 1px solid mix($light-brown, #ffffff, 50%);
      border-radius: 12px;
      background: #ffffff;
      overflow: hidden;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        border-color: $light-brown;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
      }

      &.is-expanded {
        border-color: mix($dark-blue, #ffffff, 40%);
        .chevron-icon { transform: rotate(180deg); color: $dark-blue; }
      }

      .event-card {
        display: flex;
        align-items: center;
        gap: 1.25rem;
        padding: 1rem 1.25rem;
        cursor: pointer;
        user-select: none;

        .date-badge {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-width: 70px;
          padding: 0.5rem 0.75rem;
          background: mix($white, #ffffff, 60%);
          border: 1px solid mix($light-brown, #ffffff, 60%);
          border-radius: 10px;
          text-align: center;

          .day { font-size: 1.25rem; font-weight: 800; color: $dark-blue; line-height: 1; }
          .month { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; color: $grey-back; margin-top: 0.15rem; }
          .time { font-size: 0.7rem; font-weight: 600; color: $grey-93; margin-top: 0.15rem; white-space: nowrap; }

          .badge-all-day {
            margin-top: 0.2rem;
            font-size: 0.6rem;
            font-weight: 700;
            text-transform: uppercase;
            background: mix($light-yellow, #ffffff, 50%);
            color: $brown;
            padding: 0.1rem 0.35rem;
            border-radius: 4px;
          }

          &.is-today {
            background: mix($orange, #ffffff, 12%);
            border-color: mix($orange, #ffffff, 35%);
            .day, .month { color: $orange; }
          }

          &.is-tomorrow {
            background: mix($dark-blue, #ffffff, 10%);
            border-color: mix($dark-blue, #ffffff, 25%);
            .day, .month { color: $dark-blue; }
          }
        }

        .event-info {
          flex: 1;

          .title-row {
            display: flex;
            align-items: center;
            gap: 0.5rem;

            h4 { margin: 0; font-size: 1rem; font-weight: 600; color: $secondary-color; }

            .relative-tag {
              font-size: 0.65rem;
              font-weight: 800;
              color: $orange;
              background: mix($orange, #ffffff, 15%);
              padding: 0.1rem 0.4rem;
              border-radius: 4px;
            }
          }

          .description-preview {
            margin: 0.2rem 0 0 0;
            font-size: 0.85rem;
            color: $grey-back;
            line-height: 1.4;
            display: -webkit-box;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        }

        .event-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;

          .btn-action {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            background: mix($white, #ffffff, 50%);
            border: 1px solid mix($light-brown, #ffffff, 60%);
            border-radius: 8px;
            cursor: pointer;
            color: $grey-back;

            &.edit:hover { background: mix($mid-blue, #ffffff, 10%); color: $mid-blue; border-color: mix($mid-blue, #ffffff, 30%); }
            &.delete:hover { background: mix($hot-pink, #ffffff, 15%); color: $hot-pink; border-color: mix($hot-pink, #ffffff, 40%); }
          }

          .chevron-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            color: $grey-93;
            transition: transform 0.2s ease, color 0.2s ease;
            margin-left: 0.25rem;
          }
        }
      }

      .event-details-drawer {
        border-top: 1px dashed mix($light-brown, #ffffff, 80%);
        background: mix($white, #ffffff, 30%);
        padding: 1rem 1.25rem;

        .drawer-content {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;

          .detail-row {
            display: flex;
            align-items: flex-start;
            gap: 0.5rem;
            font-size: 0.85rem;

            .detail-label { font-weight: 700; color: $dark-pink; min-width: 75px; }
            .detail-value {
              color: $secondary-color;
              font-weight: 500;

              &.countdown-pill {
                font-weight: 700;
                color: $orange;
                background: mix($orange, #ffffff, 12%);
                padding: 0.15rem 0.5rem;
                border-radius: 6px;
              }
            }

            .detail-description { margin: 0; color: $secondary-color; line-height: 1.5; white-space: pre-line; }
          }

          .drawer-actions {
            margin-top: 0.4rem;
            display: flex;
            justify-content: flex-end;

            .btn-drawer-edit {
              background: transparent;
              border: 1px solid mix($mid-blue, #ffffff, 40%);
              color: $mid-blue;
              font-size: 0.8rem;
              font-weight: 600;
              padding: 0.3rem 0.75rem;
              border-radius: 6px;
              cursor: pointer;

              &:hover { background: $mid-blue; color: $white; }
            }
          }
        }
      }
    }
  }

  .list-footer {
    display: flex;
    justify-content: center;
    margin-top: 1.25rem;

    .btn-show-more {
      background: transparent;
      border: none;
      color: $dark-blue;
      font-size: 0.85rem;
      font-weight: 600;
      cursor: pointer;
      padding: 0.4rem 0.8rem;
      border-radius: 6px;

      &:hover { background: mix($white, #ffffff, 60%); text-decoration: underline; }
    }
  }

  .empty-state {
    text-align: center;
    color: $grey-93;
    font-size: 0.95rem;
    padding: 2rem 0;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.25s ease-in-out;
  max-height: 250px;
  opacity: 1;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
  overflow: hidden;
}
</style>